import type { WeatherIcon as IconKind } from "../../shared/models";

// Our own weather glyphs: sun/moon in brass, clouds in neutral, precipitation in blue.
// Drawn on a 48px grid.

const SUN = (
  <g className="wx-sun">
    <circle cx="24" cy="24" r="8.5" />
    {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => (
      <line key={a} x1="24" y1="7" x2="24" y2="11" transform={`rotate(${a} 24 24)`} />
    ))}
  </g>
);
const SMALL_SUN = (
  <g className="wx-sun" transform="translate(-6 -6) scale(0.8)">
    <circle cx="24" cy="24" r="8" />
    {[0, 60, 120, 180, 240, 300].map((a) => (
      <line key={a} x1="24" y1="8" x2="24" y2="11.5" transform={`rotate(${a} 24 24)`} />
    ))}
  </g>
);
const MOON = <path className="wx-sun" d="M30 31a12 12 0 0 1-11-17 12 12 0 1 0 15 15 12 12 0 0 1-4 2Z" />;
const SMALL_MOON = <path className="wx-sun" d="M22 22a8 8 0 0 1-7.5-11.5A8.5 8.5 0 1 0 24 20.5a8 8 0 0 1-2 1.5Z" />;
const CLOUD = <path className="wx-cloud" d="M15 36h19a7 7 0 0 0 1-13.9A10 10 0 0 0 16 24.5 5.8 5.8 0 0 0 15 36Z" />;
const CLOUD_HIGH = <path className="wx-cloud" d="M15 30h19a7 7 0 0 0 1-13.9A10 10 0 0 0 16 18.5 5.8 5.8 0 0 0 15 30Z" />;

function drops(kind: "rain" | "snow" | "sleet") {
  const xs = [18, 25, 32];
  return xs.map((x, i) =>
    kind === "snow" || (kind === "sleet" && i === 1) ? (
      <circle key={x} className="wx-snow" cx={x - 1} cy={37 + (i % 2) * 4} r="1.6" />
    ) : (
      <line key={x} className="wx-rain" x1={x} y1={35 + (i % 2) * 3} x2={x - 2.5} y2={40 + (i % 2) * 3} />
    ),
  );
}

export function WeatherIcon({ kind, isDaytime = true, size = 48 }: { kind: IconKind; isDaytime?: boolean; size?: number | string }) {
  let body: React.ReactNode;
  switch (kind) {
    case "clear":
    case "hot":
      body = isDaytime ? SUN : MOON;
      break;
    case "partly-cloudy":
      body = (
        <>
          {isDaytime ? SMALL_SUN : SMALL_MOON}
          {CLOUD}
        </>
      );
      break;
    case "cloudy":
    case "cold":
    case "unknown":
      body = CLOUD;
      break;
    case "fog":
    case "haze":
      body = (
        <>
          {CLOUD_HIGH}
          <g className="wx-fog">
            <line x1="12" y1="35" x2="36" y2="35" />
            <line x1="15" y1="40" x2="33" y2="40" />
          </g>
        </>
      );
      break;
    case "rain":
    case "showers":
      body = (
        <>
          {kind === "showers" && (isDaytime ? SMALL_SUN : SMALL_MOON)}
          {CLOUD_HIGH}
          {drops("rain")}
        </>
      );
      break;
    case "snow":
      body = (
        <>
          {CLOUD_HIGH}
          {drops("snow")}
        </>
      );
      break;
    case "sleet":
      body = (
        <>
          {CLOUD_HIGH}
          {drops("sleet")}
        </>
      );
      break;
    case "thunder":
      body = (
        <>
          {CLOUD_HIGH}
          <path className="wx-bolt" d="M25 29l-5 8h5l-3 7 8-10h-5l3-5Z" />
        </>
      );
      break;
    case "wind":
      body = (
        <g className="wx-cloud" fill="none">
          <path d="M8 20h22a4.5 4.5 0 1 0-4.5-4.5" />
          <path d="M8 27h30a4.5 4.5 0 1 1-4.5 4.5" />
          <path d="M8 34h15" />
        </g>
      );
      break;
  }
  return (
    <svg className="wx-icon" width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
      {body}
    </svg>
  );
}
